import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  query,
  where,
  getDocs,
  limit as fbLimit,
  startAfter,
  serverTimestamp,
} from "firebase/firestore";

import { Collections } from "./constants";

/**
 * @typedef { import('./db-models').NoSQLTeam } NoSQLTeam
 */

/**
 * @param {import('firebase/firestore').Firestore} db The firestore database
 * @returns {object} The teams collection access functions
 */
export default function teamsCollectionFns(db) {
  /**
   * @param {object} options DB query options
   * @param {number} [options.limit] Max number of results to return
   * @param {import('firebase/firestore').DocumentSnapshot} [options.startAfterDoc] Document to start after for pagination
   * @returns {Promise<NoSQLTeam[]>} A list of teams
   */
  async function listTeams({ limit = 10, startAfterDoc = null } = {}) {
    try {
      let q = query(
        collection(db, Collections.TEAMS),
        where("deletedAt", "==", null),
        ...(limit > 0 ? [fbLimit(limit)] : []),
      );

      if (startAfterDoc) {
        q = query(q, startAfter(startAfterDoc));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Failed to list teams:", error);
      throw error;
    }
  }

  /**
   * @param {string} orgId The id of the organization to list for
   * @param {object} options DB query options
   * @param {number} [options.limit] Max number of results to return
   * @param {import('firebase/firestore').DocumentSnapshot} [options.startAfterDoc] Document to start after for pagination
   * @returns {Promise<NoSQLTeam[]>} A list of teams
   */
  async function listTeamsByOrganization(
    orgId,
    { limit = 10, startAfterDoc = null } = {},
  ) {
    try {
      let q = query(
        collection(db, Collections.TEAMS),
        where("organizationId", "==", orgId),
        where("deletedAt", "==", null),
        ...(limit > 0 ? [fbLimit(limit)] : []),
      );

      if (startAfterDoc) {
        q = query(q, startAfter(startAfterDoc));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Failed to list teams by organization:", error);
      throw error;
    }
  }

  /**
   * @param {string} teamId The id of the team to retrieve
   * @returns {Promise<NoSQLTeam|null>} A team or nothing
   */
  async function getTeam(teamId) {
    try {
      const docRef = doc(db, Collections.TEAMS, teamId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists() || docSnap.data().deletedAt) {
        return null;
      }

      return { id: docSnap.id, ...docSnap.data() };
    } catch (error) {
      console.error("Failed to get team:", error);
      throw error;
    }
  }

  /**
   * @param {NoSQLTeam} team The team to save
   * @returns {Promise<string>} Returns the team ID
   */
  async function saveTeam(team) {
    const { id: teamId, ...teamData } = team;

    try {
      if (teamId) {
        // Use updateDoc for existing teams to preserve fields like deletedAt
        await updateDoc(doc(db, Collections.TEAMS, teamId), teamData);
        return teamId;
      } else {
        const docRef = await addDoc(collection(db, Collections.TEAMS), {
          ...teamData,
          createdAt: serverTimestamp(),
          deletedAt: null,
        });
        return docRef.id;
      }
    } catch (error) {
      console.error("Failed to save team:", error);
      throw error;
    }
  }

  /**
   * @param {string} teamId The id of the team to delete
   * @param {string} deletedBy The id of the user who is deleting
   * @returns {Promise<void>} Nothing
   */
  async function deleteTeam(teamId, deletedBy) {
    try {
      await updateDoc(doc(db, Collections.TEAMS, teamId), {
        deletedAt: serverTimestamp(),
        deletedBy,
      });
    } catch (error) {
      console.error("Failed to delete team:", error);
      throw error;
    }
  }

  return {
    listTeams,
    listTeamsByOrganization,
    getTeam,
    saveTeam,
    deleteTeam,
  };
}
